/** Unified 5-tier key specification — client mirror of server/services/keyRegistry.js */

export const KEY_TYPES = {
  DEMO_CREATION: 'DEMO_CREATION',
  DEMO_ACCESS: 'DEMO_ACCESS',
  PAID_SUBSCRIPTION: 'PAID_SUBSCRIPTION',
  ORG_MENTEE: 'ORG_MENTEE',
  MENTOR_ACTIVATION: 'MENTOR_ACTIVATION',
  PROCUREMENT_DEMO: 'PROCUREMENT_DEMO',
} as const;

export type KeyType = (typeof KEY_TYPES)[keyof typeof KEY_TYPES];

export const REGEX = {
  DEMO_CREATION: /^(PRO|ELT)-[A-Z]{3}$/,
  DEMO_ACCESS: /^(PRO|ELT)[A-Z]{3}$/,
  PAID_PRO: /^PRO-\d{4}$/,
  PAID_ORG: /^ELT-[A-Z]{3}-\d{4}$/,
  ORG_MENTEE: /^ELT-\d{4}$/,
  MENTOR_ACTIVATION: /^ELTM-[A-Z0-9]{4}$/,
  PROCUREMENT_DEMO: /^ELTD[A-Z]{3}$/,
} as const;

export function normalizeKey(raw: string) {
  return raw.replace(/\s/g, '').toUpperCase();
}

export function isDemoCreationCode(raw: string) {
  return REGEX.DEMO_CREATION.test(normalizeKey(raw));
}

export function isDemoAccessKey(raw: string) {
  return REGEX.DEMO_ACCESS.test(normalizeKey(raw));
}

export function isPaidProKey(raw: string) {
  return REGEX.PAID_PRO.test(normalizeKey(raw));
}

export function isPaidOrgKey(raw: string) {
  return REGEX.PAID_ORG.test(normalizeKey(raw));
}

export function isPaidSubscriptionKey(raw: string) {
  const key = normalizeKey(raw);
  return isPaidProKey(key) || isPaidOrgKey(key);
}

export function isOrgMenteeKey(raw: string) {
  return REGEX.ORG_MENTEE.test(normalizeKey(raw));
}

export function isMentorActivationKey(raw: string) {
  return REGEX.MENTOR_ACTIVATION.test(normalizeKey(raw));
}

export const PROTECTED_SANDBOX_ORG_TOKEN = 'ELT-DEV-9842';

export function isPortalLoginKey(raw: string) {
  const key = normalizeKey(raw);
  if (isDemoCreationCode(key)) return false;
  return isDemoAccessKey(key)
    || isPaidSubscriptionKey(key)
    || isOrgMenteeKey(key)
    || isProcurementDemoKey(key);
}

export function isProcurementDemoKey(raw: string) {
  return REGEX.PROCUREMENT_DEMO.test(normalizeKey(raw));
}

/** Admin-issued keys that must sign on Screen-ELT13 (not minted at New Registration). */
export function isIssuedAccessKey(raw: string) {
  const key = normalizeKey(raw);
  return isOrgMenteeKey(key) || isMentorActivationKey(key);
}

/** Registration Key Entrance — mentee/org keys plus human mentor activation keys. */
export function isPortalEntranceKey(raw: string) {
  const key = normalizeKey(raw);
  return isPortalLoginKey(key) || isMentorActivationKey(key) || isProcurementDemoKey(key);
}

export function orgCodeFromCompanyName(companyName: string) {
  const letters = companyName.replace(/[^a-zA-Z]/g, '').toUpperCase();
  return (letters + 'XXX').slice(0, 3);
}

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

function randomLetters(count: number) {
  let out = '';
  for (let i = 0; i < count; i += 1) {
    out += LETTERS[Math.floor(Math.random() * 26)];
  }
  return out;
}

export function mintDemoCreationCode(prefix: 'PRO' | 'ELT') {
  return `${prefix}-${randomLetters(3)}`;
}

export function demoCreationTrack(raw: string): 'PRO' | 'ELT' | null {
  const key = normalizeKey(raw);
  if (key.startsWith('ELT-')) return 'ELT';
  if (key.startsWith('PRO-')) return 'PRO';
  return null;
}

/** Route hints for briefing room entry based on stored access key. */
export function isProBriefingKey(raw: string) {
  const key = normalizeKey(raw);
  return (isDemoAccessKey(key) && key.startsWith('PRO')) || isPaidProKey(key);
}

export function isEltBriefingKey(raw: string) {
  const key = normalizeKey(raw);
  return (isDemoAccessKey(key) && key.startsWith('ELT'))
    || isOrgMenteeKey(key)
    || isPaidOrgKey(key);
}
